<template>
  <el-dialog
    title="生成支付订单"
    :visible.sync="dialogVisible"
    width="920px"
    custom-class="create-pay-order-from-po-dialog"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <div class="pay-order-section">
      <div class="section-title">产品明细</div>
      <el-table
        :data="previewList"
        border
        stripe
        size="mini"
        show-summary
        :summary-method="getSummaries"
        v-loading="loading"
        element-loading-text="加载中..."
        max-height="320"
      >
        <el-table-column label="序号" width="50" align="center" prop="index"></el-table-column>
        <el-table-column prop="materialName" label="材料名称" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.materialName || scope.row.productName || "-" }}</template>
        </el-table-column>
        <el-table-column prop="purchaseQuantity" label="采购数量" width="80" align="right"></el-table-column>
        <el-table-column prop="unitPrice" label="单价" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column prop="purchaseAmount" label="采购金额" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.purchaseAmount) }}</template>
        </el-table-column>
        <el-table-column prop="taxRate" label="税率" width="70" align="right">
          <template slot-scope="scope">{{ formatTaxRate(supplierTaxRate) }}</template>
        </el-table-column>
        <el-table-column prop="taxAmount" label="税费" width="80" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.taxAmount) }}</template>
        </el-table-column>
        <el-table-column prop="payableAmount" label="应付金额" width="90" align="right">
          <template slot-scope="scope">{{ formatMoney(scope.row.payableAmount) }}</template>
        </el-table-column>
      </el-table>
    </div>

    <div class="pay-order-form">
      <div class="section-title">支付信息</div>
      <div class="form-grid">
        <div class="form-item wide">
          <span class="form-label">采购供应商</span>
          <span class="readonly-value">{{ supplierName || "-" }}</span>
          <span class="tax-label">税率：{{ formatTaxRate(supplierTaxRate) }}</span>
        </div>
        <div class="form-item">
          <span class="form-label">运费</span>
          <el-input v-model.number="form.freight" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item">
          <span class="form-label">平台优惠</span>
          <el-input v-model.number="form.platformDiscount" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item">
          <span class="form-label">退换货费</span>
          <el-input v-model.number="form.returnExchangeFee" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item">
          <span class="form-label">其他费用</span>
          <el-input v-model.number="form.otherFee" size="small" type="number" @input="calcTotal"></el-input>
        </div>
        <div class="form-item wide">
          <span class="form-label">下单平台</span>
          <el-radio-group v-model="form.orderPlatform" size="small">
            <el-radio label="对公">对公</el-radio>
            <el-radio label="平台">平台</el-radio>
          </el-radio-group>
        </div>
        <div class="form-item wide">
          <span class="form-label">订单确认证明</span>
          <div v-if="orderConfirmProof.length" class="proof-thumb-list">
            <el-image
              v-for="(url, index) in orderConfirmProof"
              :key="url + '-' + index"
              :src="url"
              :preview-src-list="orderConfirmProof"
              fit="cover"
              class="proof-thumb"
            ></el-image>
          </div>
          <span v-else class="readonly-value proof-empty">未上传（生成后可在支付订单中补充）</span>
        </div>
        <div class="form-item wide remark-item">
          <span class="form-label">备注</span>
          <span v-if="remark" class="readonly-value remark-text">{{ remark }}</span>
          <span v-else class="readonly-value proof-empty">无</span>
        </div>
      </div>
      <div class="amount-summary">
        <div class="amount-row">
          <span class="amount-label">应付金额</span>
          <span class="amount-value payable">{{ formatMoney(form.payableAmount) }}</span>
        </div>
        <div class="amount-row">
          <span class="amount-label">实付金额</span>
          <el-input v-model.number="form.actualPaidAmount" size="small" type="number" class="amount-input" @input="onActualPaidInput"></el-input>
        </div>
        <div class="amount-row">
          <span class="amount-label">差额</span>
          <span class="amount-value" :class="amountDiffClass">{{ formatAmountDiff(amountDiff) }}</span>
          <span class="amount-tip">（实付金额 - 应付金额）</span>
        </div>
      </div>
      <div class="preview-tip">将为选中采购单生成一张支付订单；同供应商、同店铺可合单；采购单订单确认证明与备注将一并带入；生成后采购单进入待支付</div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" size="small" :loading="submitting" :disabled="!previewList.length" @click="submit">确 定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { payOrderModules } from "@r";
import { PAY_ORDER_SOURCE_TYPE, PAY_ORDER_STATUS } from "../../constants/payOrderStatus";

function createEmptyForm() {
  return {
    freight: 0,
    platformDiscount: 0,
    returnExchangeFee: 0,
    otherFee: 0,
    orderPlatform: "对公",
    actualPaidAmount: 0,
    payableAmount: 0,
  };
}

export default {
  name: "CreatePayOrderFromPurchaseOrderDialog",
  data() {
    return {
      dialogVisible: false,
      loading: false,
      submitting: false,
      purchaseOrderIds: [],
      previewList: [],
      supplierId: "",
      supplierName: "",
      supplierTaxRate: 0,
      orderConfirmProof: [],
      remark: "",
      shouldSyncActualPaid: true,
      form: createEmptyForm(),
    };
  },
  computed: {
    amountDiff() {
      const actualPaidAmount = parseFloat(this.form.actualPaidAmount) || 0;
      const payableAmount = parseFloat(this.form.payableAmount) || 0;
      return parseFloat((actualPaidAmount - payableAmount).toFixed(2));
    },
    amountDiffClass() {
      if (this.amountDiff > 0) return "positive";
      if (this.amountDiff < 0) return "negative";
      return "zero";
    },
  },
  methods: {
    open(rows) {
      const ids = [];
      const idSet = new Set();
      const feeOrders = [];
      (rows || []).forEach(row => {
        const id = String((row && (row.orderId || row._id)) || "");
        if (!id || idSet.has(id)) return;
        idSet.add(id);
        ids.push(id);
        feeOrders.push(row);
      });
      this.purchaseOrderIds = ids;
      this.previewList = [];
      this.supplierId = "";
      this.supplierName = "";
      this.supplierTaxRate = 0;
      this.orderConfirmProof = [];
      this.remark = "";
      this.form = createEmptyForm();
      // 先用选中采购单费用预填，预览接口会再校正（多单时汇总）
      this.form.freight = feeOrders.reduce((sum, row) => sum + (parseFloat(row.freight) || 0), 0);
      this.form.platformDiscount = feeOrders.reduce((sum, row) => sum + (parseFloat(row.platformDiscount) || 0), 0);
      this.form.returnExchangeFee = feeOrders.reduce((sum, row) => sum + (parseFloat(row.returnExchangeFee) || 0), 0);
      this.form.otherFee = feeOrders.reduce((sum, row) => sum + (parseFloat(row.otherFee) || 0), 0);
      const platform = feeOrders.map(row => row.orderPlatform).find(Boolean);
      if (platform) {
        this.form.orderPlatform = platform;
      }
      this.shouldSyncActualPaid = true;
      this.dialogVisible = true;
      this.loadPreview();
    },
    buildParams({ includeFees = true } = {}) {
      const params = {
        sourceType: PAY_ORDER_SOURCE_TYPE.PURCHASE_ORDER,
        purchaseOrderIds: JSON.stringify(this.purchaseOrderIds),
      };
      if (includeFees) {
        params.freight = this.form.freight;
        params.platformDiscount = this.form.platformDiscount;
        params.returnExchangeFee = this.form.returnExchangeFee;
        params.otherFee = this.form.otherFee;
        params.orderPlatform = this.form.orderPlatform;
        params.actualPaidAmount = this.form.actualPaidAmount;
      }
      return params;
    },
    loadPreview() {
      if (!this.purchaseOrderIds.length) {
        this.$message.warning("请先选择采购单");
        this.dialogVisible = false;
        return;
      }
      this.loading = true;
      payOrderModules
        // 预览不传费用字段，避免默认 0 覆盖采购单已有运费等
        .previewPayOrder(this.buildParams({ includeFees: false }))
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "加载产品明细失败");
            this.previewList = [];
            this.form.payableAmount = 0;
            return;
          }
          const mdata = res.mdata || {};
          this.supplierId = mdata.supplierId || "";
          this.supplierName = mdata.supplierName || "";
          this.supplierTaxRate = parseFloat(mdata.taxRate) || 0;
          this.orderConfirmProof = Array.isArray(mdata.orderConfirmProof) ? mdata.orderConfirmProof.filter(Boolean) : [];
          this.remark = mdata.remark || "";
          this.form.freight = mdata.freight != null ? mdata.freight : this.form.freight;
          this.form.platformDiscount = mdata.platformDiscount != null ? mdata.platformDiscount : this.form.platformDiscount;
          this.form.returnExchangeFee = mdata.returnExchangeFee != null ? mdata.returnExchangeFee : this.form.returnExchangeFee;
          this.form.otherFee = mdata.otherFee != null ? mdata.otherFee : this.form.otherFee;
          this.form.orderPlatform = mdata.orderPlatform || this.form.orderPlatform;
          this.previewList = (res.ldata || []).map((row, index) => ({
            ...row,
            index: index + 1,
          }));
          this.calcTotal();
          const actualFromOrder = mdata.actualPaidAmount;
          if (actualFromOrder != null && actualFromOrder !== "" && Number(actualFromOrder) > 0) {
            this.form.actualPaidAmount = actualFromOrder;
            this.shouldSyncActualPaid = false;
          } else {
            this.syncActualPaidAmount();
          }
        })
        .catch(err => {
          this.$message.error((err && err.message) || "加载产品明细失败");
          this.previewList = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    calcTotal() {
      const linePayableAmount = (this.previewList || []).reduce((sum, row) => sum + (Number(row.payableAmount) || 0), 0);
      const freight = parseFloat(this.form.freight) || 0;
      const platformDiscount = parseFloat(this.form.platformDiscount) || 0;
      const returnExchangeFee = parseFloat(this.form.returnExchangeFee) || 0;
      const otherFee = parseFloat(this.form.otherFee) || 0;
      this.form.payableAmount = parseFloat((linePayableAmount + freight - platformDiscount + returnExchangeFee + otherFee).toFixed(2));
      this.syncActualPaidAmount();
    },
    onActualPaidInput() {
      this.shouldSyncActualPaid = false;
      if (this.form.actualPaidAmount === "" || this.form.actualPaidAmount === null) {
        this.form.actualPaidAmount = 0;
      }
    },
    syncActualPaidAmount() {
      if (this.shouldSyncActualPaid) {
        this.form.actualPaidAmount = this.form.payableAmount;
      }
    },
    submit() {
      if (!this.previewList.length) {
        this.$message.error("没有可生成的采购数据");
        return;
      }
      this.submitting = true;
      payOrderModules
        .createPayOrder({
          ...this.buildParams(),
          orderStatus: PAY_ORDER_STATUS.PENDING_ORDER,
        })
        .then(res => {
          if (res.result !== 1) {
            this.$message.error(res.message || "生成失败");
            return;
          }
          this.$message.success("支付订单生成成功");
          this.dialogVisible = false;
          this.$emit("success", res.mdata || {});
        })
        .catch(err => {
          this.$message.error((err && err.message) || "生成失败");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    formatMoney(value) {
      const num = parseFloat(value);
      return Number.isNaN(num) ? "0.00" : num.toFixed(2);
    },
    formatTaxRate(value) {
      const num = parseFloat(value);
      if (Number.isNaN(num)) return "0%";
      return `${parseFloat((num * 100).toFixed(2))}%`;
    },
    formatAmountDiff(value) {
      const num = parseFloat(value) || 0;
      return (num > 0 ? "+" : "") + num.toFixed(2);
    },
    getSummaries({ columns, data }) {
      const sums = [];
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = "合计";
          return;
        }
        if (["purchaseQuantity", "purchaseAmount", "taxAmount", "payableAmount"].includes(column.property)) {
          const total = (data || []).reduce((sum, row) => sum + (parseFloat(row[column.property]) || 0), 0);
          sums[index] = ["purchaseAmount", "taxAmount", "payableAmount"].includes(column.property)
            ? this.formatMoney(total)
            : total;
          return;
        }
        sums[index] = "";
      });
      return sums;
    },
    handleClose() {
      this.previewList = [];
      this.purchaseOrderIds = [];
      this.orderConfirmProof = [];
      this.remark = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.create-pay-order-from-po-dialog {
  .pay-order-section,
  .pay-order-form {
    margin-bottom: 16px;
  }
  .section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }
  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;
  }
  .form-item {
    display: flex;
    align-items: center;
    gap: 8px;
    &.wide {
      grid-column: 1 / -1;
    }
    .el-input {
      flex: 1;
      min-width: 0;
    }
  }
  .form-label {
    width: 96px;
    flex-shrink: 0;
    color: #606266;
  }
  .readonly-value {
    min-width: 160px;
    color: #303133;
  }
  .proof-empty {
    color: #909399;
  }
  .remark-item {
    align-items: flex-start;
  }
  .remark-text {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
  }
  .proof-thumb-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .proof-thumb {
    width: 56px;
    height: 56px;
    border-radius: 4px;
    border: 1px solid #ebeef5;
  }
  .tax-label {
    margin-left: 8px;
    color: #909399;
    font-size: 12px;
  }
  .amount-summary {
    margin-top: 14px;
    padding: 12px;
    background: #f5f7fa;
    border-radius: 4px;
  }
  .amount-row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .amount-label {
    width: 80px;
    color: #606266;
  }
  .amount-value {
    font-weight: 600;
    &.payable {
      color: #e6a23c;
    }
    &.positive {
      color: #67c23a;
    }
    &.negative {
      color: #f56c6c;
    }
  }
  .amount-input {
    width: 160px;
  }
  .amount-tip {
    margin-left: 8px;
    color: #909399;
    font-size: 12px;
  }
  .preview-tip {
    margin-top: 10px;
    color: #909399;
    font-size: 12px;
  }
}
</style>
